import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import "./AdminSupport.css";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";

import SupportChat from "./SupportChat";
import SupportSidebar from "./SupportSidebar";

import socket from "../../../../../socket/socket";

import {
  playNotification,
} from "../../../../common/Notification";

import useAuth from "../../../../../hooks/useAuth";

const AdminSupport = () => {
  const { user } = useAuth();

  const api = useMemo(
    () => new API(),
    []
  );

  const [loading, setLoading] =
    useState(false);

  const [tickets, setTickets] =
    useState([]);

  const [
    selectedTicket,
    setSelectedTicket,
  ] = useState(null);

  const [messages, setMessages] =
    useState([]);

  const [typing, setTyping] =
    useState(false);

  const [roomJoined, setRoomJoined] =
    useState(false);

  const adminId =
    user?._id ||
    user?.id ||
    user?.userId;

  const adminRole =
    user?.role ||
    user?.userType ||
    "Admin";

  const selectedTicketId =
    selectedTicket?._id ||
    selectedTicket?.id;

  /*
  |--------------------------------------------------------------------------
  | Load Tickets
  |--------------------------------------------------------------------------
  */

  const loadTickets =
    useCallback(
      async ({
        preserveSelection = true,
      } = {}) => {
        try {
          setLoading(true);

          const response =
            await api.get(
              config
                .getAdminSupportTickets
            );

          if (
            response.status !==
              "success" ||
            !Array.isArray(
              response.data
            )
          ) {
            return;
          }

          const fetchedTickets =
            response.data;

          setTickets(
            fetchedTickets
          );

          setSelectedTicket(
            (currentTicket) => {
              if (
                !fetchedTickets.length
              ) {
                return null;
              }

              if (
                preserveSelection &&
                currentTicket
              ) {
                const currentId =
                  currentTicket._id ||
                  currentTicket.id;

                const updatedTicket =
                  fetchedTickets.find(
                    (ticket) =>
                      String(
                        ticket._id ||
                          ticket.id
                      ) ===
                      String(
                        currentId
                      )
                  );

                if (updatedTicket) {
                  return updatedTicket;
                }
              }

              return fetchedTickets[0];
            }
          );
        } catch (error) {
          console.error(
            "Load support tickets error:",
            error
          );
        } finally {
          setLoading(false);
        }
      },
      [api]
    );

  useEffect(() => {
    loadTickets({
      preserveSelection: false,
    });
  }, [loadTickets]);

  /*
  |--------------------------------------------------------------------------
  | Socket Connection
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!adminId) {
      return undefined;
    }

    const joinAdmin = () => {
      socket.emit("join", {
        userId: adminId,
        role: adminRole,
      });
    };

    const handleConnect = () => {

      joinAdmin();
    };

    const handleConnectError = (
      error
    ) => {
      console.error(
        "Admin socket connection error:",
        error.message
      );
    };

    socket.on(
      "connect",
      handleConnect
    );

    socket.on(
      "connect_error",
      handleConnectError
    );

    if (!socket.connected) {
      socket.connect();
    } else {
      joinAdmin();
    }

    return () => {
      socket.off(
        "connect",
        handleConnect
      );

      socket.off(
        "connect_error",
        handleConnectError
      );

      /*
       * Do not call socket.disconnect()
       * here if this is a shared socket
       * used by other modules.
       */
    };
  }, [
    adminId,
    adminRole,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Load Messages
  |--------------------------------------------------------------------------
  */

  const loadMessages =
    useCallback(
      async (
        ticketId
      ) => {
        if (!ticketId) {
          return;
        }

        try {
          const response =
            await api.get(
              `${config.getAdminSupportMessages}/${ticketId}`
            );

          if (
            response.status ===
            "success"
          ) {
            const responseMessages =
              Array.isArray(
                response.data
              )
                ? response.data
                : response.data
                    ?.messages || [];

            setMessages(
              responseMessages
            );
          }
        } catch (error) {
          console.error(
            "Load support messages error:",
            error
          );
        }
      },
      [api]
    );

  /*
  |--------------------------------------------------------------------------
  | Join Selected Ticket Room
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      setRoomJoined(false);

      return undefined;
    }

    let active = true;

    setMessages([]);
    setTyping(false);
    setRoomJoined(false);

    const joinTicketRoom =
      () => {
        socket.emit(
          "join-ticket",
          {
            ticketId:
              selectedTicketId,
          },
          (response) => {
            if (!active) {
              return;
            }

            if (
              response?.success ===
              false
            ) {
              console.error(
                "Admin could not join ticket:",
                response.message
              );

              setRoomJoined(false);
              return;
            }

            setRoomJoined(true);
          }
        );

        /*
         * Keep this fallback only when
         * backend does not send callback.
         */
        setRoomJoined(true);
      };

    if (socket.connected) {
      joinTicketRoom();
    } else {
      socket.once(
        "connect",
        joinTicketRoom
      );
    }

    loadMessages(
      selectedTicketId
    );

    return () => {
      active = false;

      socket.off(
        "connect",
        joinTicketRoom
      );

      if (socket.connected) {
        socket.emit(
          "leave-ticket",
          {
            ticketId:
              selectedTicketId,
          }
        );
      }

      setTyping(false);
      setRoomJoined(false);
    };
  }, [
    selectedTicketId,
    loadMessages,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Typing Events
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleTyping = (
      data = {}
    ) => {
      const eventTicketId =
        data.ticketId?._id ||
        data.ticketId;

      if (
        eventTicketId &&
        selectedTicketId &&
        String(eventTicketId) !==
          String(
            selectedTicketId
          )
      ) {
        return;
      }

      const sender =
        String(
          data.sender || ""
        ).toLowerCase();

      if (
        sender === "user" ||
        sender === "customer"
      ) {
        setTyping(true);
      }
    };

    const handleStopTyping = (
      data = {}
    ) => {
      const eventTicketId =
        data.ticketId?._id ||
        data.ticketId;

      if (
        eventTicketId &&
        selectedTicketId &&
        String(eventTicketId) !==
          String(
            selectedTicketId
          )
      ) {
        return;
      }

      setTyping(false);
    };

    socket.on(
      "typing",
      handleTyping
    );

    socket.on(
      "stop-typing",
      handleStopTyping
    );

    return () => {
      socket.off(
        "typing",
        handleTyping
      );

      socket.off(
        "stop-typing",
        handleStopTyping
      );
    };
  }, [selectedTicketId]);

  /*
  |--------------------------------------------------------------------------
  | Receive Messages
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const receiveMessage = (
      newMessage
    ) => {
      if (!newMessage) {
        return;
      }

      const messageTicketId =
        newMessage.ticketId?._id ||
        newMessage.ticketId ||
        newMessage.ticket?._id ||
        newMessage.ticket?.id;

      const isSelectedTicket =
        selectedTicketId &&
        messageTicketId &&
        String(
          messageTicketId
        ) ===
          String(
            selectedTicketId
          );

      if (isSelectedTicket) {
        setMessages(
          (previous) => {
            const newMessageId =
              newMessage._id ||
              newMessage.id;

            const exists =
              newMessageId &&
              previous.some(
                (item) =>
                  String(
                    item._id ||
                      item.id
                  ) ===
                  String(
                    newMessageId
                  )
              );

            if (exists) {
              return previous;
            }

            return [
              ...previous,
              newMessage,
            ];
          }
        );

        setTyping(false);
      }

      const senderType =
        String(
          newMessage.senderType ||
            ""
        ).toLowerCase();

      if (
        senderType === "user" ||
        senderType ===
          "customer"
      ) {
        playNotification();
      }

      /*
       * Refresh sidebar for latest
       * message, unread count and order.
       */
      loadTickets({
        preserveSelection: true,
      });
    };

    socket.on(
      "receiveMessage",
      receiveMessage
    );

    return () => {
      socket.off(
        "receiveMessage",
        receiveMessage
      );
    };
  }, [
    selectedTicketId,
    loadTickets,
  ]);

  return (
    <div className="admin-support-container">
      <SupportSidebar
        tickets={tickets}
        setTickets={setTickets}
        selectedTicket={
          selectedTicket
        }
        setSelectedTicket={
          setSelectedTicket
        }
        loading={loading}
      />

      <SupportChat
        selectedTicket={
          selectedTicket
        }
        messages={messages}
        setMessages={
          setMessages
        }
        typing={typing}
        roomJoined={
          roomJoined
        }
        loadTickets={
          loadTickets
        }
      />
    </div>
  );
};

export default AdminSupport;